import { useEffect } from 'react';

import { AskTab } from '@/components/ask/AskTab';
import { GraphTab } from '@/components/graph/GraphTab';
import { FolderView } from '@/components/files/FolderView';
import { HomeTab } from '@/components/home/HomeTab';
import { OrbitTab } from '@/components/orbit/OrbitTab';
import { MarkdownEditor } from '@/components/reader/MarkdownEditor';
import { ReaderTab } from '@/components/reader/ReaderTab';
import { useDocument, useNode } from '@/hooks/queries';
import { useShell } from '@/stores/shell';
import type { TabDesc } from '@/stores/tabs';

import { workspace } from '../controller';
import { useWorkspaceStore } from '../store';

/**
 * Is this panel the foreground content right now? A TABBED panel paints only
 * when it's the dock's active tab; a floating/snapped/maximized panel is
 * always its own visible window regardless of dock activity — so ForceGraph
 * panels (Atlas/Orbits) can be simultaneously live. Read straight from the
 * workspace store rather than a single top-down `visible` prop threaded from
 * App.tsx (there is no longer one "active tab" for the whole app — every
 * panel decides its own paint-gating from its own mode/activity).
 */
function usePanelVisible(panelId: string): boolean {
  return useWorkspaceStore((s) => {
    const p = s.panels[panelId];
    if (!p) return false;
    // A maximized panel covers the whole surface, so nothing behind it is worth
    // painting.
    if (s.maximizedId && s.maximizedId !== panelId) return false;
    if (p.mode !== 'tabbed') return true;
    // A tabbed panel is visible when it is the dock's FRONT TAB — not when it
    // is the app's focused panel. Those diverge the moment a floating window
    // takes focus, and using `activeId` here meant the tab behind that window
    // was told it was hidden and stopped painting: the atlas canvas, the orbit
    // canvas and the reader all went blank behind any floating panel.
    return s.activeTabId === panelId;
  });
}

const onAddWebsite = () => useShell.getState().openWebsite();

function GraphPanel({ panelId }: { panelId: string }) {
  const visible = usePanelVisible(panelId);
  return <GraphTab visible={visible} onAddWebsite={onAddWebsite} />;
}

function OrbitPanel({ panelId }: { panelId: string }) {
  const visible = usePanelVisible(panelId);
  return <OrbitTab visible={visible} onAddWebsite={onAddWebsite} />;
}

/** A vault document: the real reader (PDF/canvas/text), not a read-only
 * markdown stand-in. Its tab title stays reactive to the document's title. */
function DocPanel({ panelId }: { panelId: string }) {
  const visible = usePanelVisible(panelId);
  const docId = useWorkspaceStore((s) => (s.panels[panelId]?.data?.docId as string | undefined) ?? null);
  const docKind = useWorkspaceStore((s) => (s.panels[panelId]?.data?.docKind as string | undefined) ?? 'document');
  const isDocument = docKind === 'document';
  const doc = useDocument(isDocument ? (docId ?? undefined) : undefined);
  // The panel's CURRENT title, so the sync below also re-fires when the title
  // was clobbered (e.g. a deep-link/Back-Forward re-open blanked it) even
  // though the cached document query's title never changed. `setTitle` no-ops
  // when already equal, so this can't loop.
  const panelTitle = useWorkspaceStore((s) => s.panels[panelId]?.title);

  useEffect(() => {
    if (doc.data?.title) workspace.setTitle(panelId, doc.data.title);
  }, [panelId, doc.data?.title, panelTitle]);

  if (!docId) {
    return (
      <div className="grid h-full place-items-center">
        <div className="wp-reader wp-reader--empty">Select a file from the Explorer.</div>
      </div>
    );
  }
  // A layout persisted before markdown-backed nodes got their own panel type
  // may still carry a note/org/AI-output under the generic 'doc' type — open
  // it as a note instead of 404ing the reader against a non-document id. Thread
  // THIS panel's id so a 404 self-close closes this `doc:` panel (its computed
  // `markdown:`/`doc:` fallback id wouldn't match a legacy doc-panel mount).
  if (!isDocument) return <MarkdownEditor nodeId={docId} visible={visible} panelId={panelId} />;
  return <ReaderTab docId={docId} visible={visible} panelId={panelId} />;
}

/** A note / org hub / AI-output's rich markdown body — the real editor. */
function MarkdownPanel({ panelId }: { panelId: string }) {
  const visible = usePanelVisible(panelId);
  const nodeId = useWorkspaceStore((s) => (s.panels[panelId]?.data?.docId as string | undefined) ?? null);
  const node = useNode(nodeId ?? undefined);
  // See DocPanel: re-fire the title sync when the panel title itself changes,
  // not only when the (cacheable) node label does.
  const panelTitle = useWorkspaceStore((s) => s.panels[panelId]?.title);

  useEffect(() => {
    if (node.data?.label) workspace.setTitle(panelId, node.data.label);
  }, [panelId, node.data?.label, panelTitle]);

  if (!nodeId) return null;
  return <MarkdownEditor nodeId={nodeId} visible={visible} panelId={panelId} />;
}

/** An ask conversation. Scope/seed/conversation ride in the panel's `data`
 * (set by `workspace.openAsk` / the `stores/tabs` shim) — projected here into
 * the `TabDesc` shape `AskTab` already expects. */
function AskPanel({ panelId }: { panelId: string }) {
  const visible = usePanelVisible(panelId);
  const tab = useWorkspaceStore((s): TabDesc | null => {
    const p = s.panels[panelId];
    if (!p) return null;
    const data = p.data ?? {};
    return {
      id: p.id,
      kind: 'ask',
      title: p.title,
      scopeDocIds: data.scopeDocIds as string[] | undefined,
      scopeNodeIds: data.scopeNodeIds as string[] | undefined,
      seedQuery: data.seedQuery as string | undefined,
      conversationId: data.conversationId as string | undefined,
    };
  });
  if (!tab) return null;
  return <AskTab tab={tab} visible={visible} />;
}

/**
 * Routes a panel to the REAL surface for its type — `GraphTab`/`OrbitTab`/
 * `ReaderTab`/`MarkdownEditor`/`AskTab`, each keeping its own Base-UI/shadcn
 * UI intact. Only Dockview's tab strip sits "on top"; this is never the old
 * simplified read-only "workview pane".
 */
export function PanelContent({ panelId }: { panelId: string }) {
  const type = useWorkspaceStore((s) => s.panels[panelId]?.type);
  switch (type) {
    case 'home':
      return <HomeTab />;
    case 'files':
      return <FolderView />;
    case 'graph':
      return <GraphPanel panelId={panelId} />;
    case 'orbit':
      return <OrbitPanel panelId={panelId} />;
    case 'ask':
      return <AskPanel panelId={panelId} />;
    case 'markdown':
      return <MarkdownPanel panelId={panelId} />;
    case 'doc':
      return <DocPanel panelId={panelId} />;
    default:
      return null;
  }
}
